// /pricing — the standalone, linkable pricing page (per-page research 2026-08-02, top-leverage
// item #1: a procurement committee needs a URL it can forward, and the competitive teardown found
// only quote-gated or self-contradicting pricing across the Indian school-ERP market).
// Fixed self-serve prices render only from the reconciled billing catalogue; per-student bands are
// a separately labelled proposal model. The live entitlement matrix remains authoritative for
// access, while release readiness is displayed separately. Terms use registry-reviewed wording.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import {
  ArrowRight, Check, Landmark, ShieldCheck, CalendarCheck, DatabaseBackup, HelpCircle,
} from "lucide-react";
import { getInitialMarketingContent, getMarketingContent } from "../lib/marketingContent";
import { MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import ButtonLink from "../components/marketing/ButtonLink";
import { ORGANIZATION_ID } from "../components/marketing/organizationSchema";
import { Button, ErrorState, Skeleton } from "../components/ui/primitives";
import { fromConfig } from "../lib/pricingBands";
import {
  PlanCard,
  buildFixedCards,
  mapMatrixPlans,
  pricingTaxDisplay,
} from "./landingparts/pricingShared";
import PricingEstimator from "./landingparts/PricingEstimator";
import PricingCostDisclosure from "./landingparts/PricingCostDisclosure";
import PricingFeatureCatalogue from "./landingparts/PricingFeatureCatalogue";
import PerStudentPricingModal, {
  hasVerifiedPerStudentPricingModel,
  PRICING_FEATURE_STATUS,
  projectPerStudentPlanFeatures,
} from "../components/marketing/PerStudentPricingModal";
import { init as initAnalytics, track } from "../lib/analytics";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";

// Registry-verified terms only — the answers a committee looks for and quote-gated
// competitors never publish. (P4 trial · C3 self-serve · S5 export/erasure · X1 BYOG.)
const TERMS = [
  [CalendarCheck, "The trial is a full term", "The 90-day trial does not become paid merely because time passes. Paid service can begin at expiry only after an authorised representative selects a plan and completes the required mandate or checkout."],
  [Landmark, "Published charges, invoice-bound evidence", "The commercial ledger separates payment-method MDR from the Vidyapeeth360 platform fee. Collection still runs through your school's connected Razorpay or Cashfree account; actual settlement and final bank credit remain in provider and bank evidence."],
  [DatabaseBackup, "Data access and exit are documented", "Use supported exports while access is active, and request access or erasure through the documented process. Retention, legal holds, backups and deletion timing follow the applicable agreement and India's phased DPDP framework."],
  [ShieldCheck, "Commercial terms are written down", "Your confirmed schedule records the base price, billing cycle, tax treatment, limits and any negotiated scope before subscription."],
];

export default function PricingPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // Seed from the prerender snapshot's embedded payload SYNCHRONOUSLY: the first hydrated
  // render must match the snapshot exactly, or the priced grid repaints and shifts (the
  // Lighthouse desktop CLS failure). Falls back to null (then the shared fetch) off-snapshot.
  const [cms, setCms] = useState(getInitialMarketingContent);
  const [cmsError, setCmsError] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [perStudentOpen, setPerStudentOpen] = useState(false);

  const loadCms = useCallback(() => {
    setCmsError(false);
    // Shared session-memoized fetch (lib/marketingContent) — the SiteFooter's own CMS read
    // rides the same inflight promise, so the page issues ONE /public/marketing request. A
    // failure resolves null AND clears the memo, so this retry path refetches for real.
    getMarketingContent().then((data) => (data ? setCms(data) : setCmsError(true)));
  }, []);
  useEffect(() => { loadCms(); }, [loadCms]);
  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
    track("pricing_view", { placement: "pricing_page" });
  }, []);

  const priceCfg = useMemo(() => fromConfig(cms?.pricing_model), [cms]);
  const fixedCards = useMemo(() => buildFixedCards(priceCfg, cms?.pricing_integrity), [priceCfg, cms]);
  const matrixPlans = useMemo(() => mapMatrixPlans(cms?.plan_matrix?.plans, fixedCards), [cms, fixedCards]);
  const perStudentPricingVerified = useMemo(
    () => hasVerifiedPerStudentPricingModel(cms?.pricing_model, cms?.pricing_model_provenance),
    [cms],
  );
  const bandRates = perStudentPricingVerified ? priceCfg.bands.map((b) => b.ratePaise) : [];
  const bandRangeLabel = bandRates.length > 0
    ? ` (₹${Math.min(...bandRates) / 100}–₹${Math.max(...bandRates) / 100} a month)`
    : "";
  const commercialStatus = cms?.pricing_integrity?.status || "unavailable";
  const taxDisplay = pricingTaxDisplay(cms?.pricing_integrity);
  // WP-04: the catalogue-integrity check stays, but its output is an ENGINEERING signal —
  // it goes to the console/monitoring, never to a public banner above the plan cards.
  useEffect(() => {
    if (cms && commercialStatus !== "aligned") {
      // eslint-disable-next-line no-console
      console.warn(`[pricing] catalogue integrity: status=${commercialStatus} — proposal configuration and self-serve billing catalogue disagree; resolve in Platform Admin.`);
    }
  }, [cms, commercialStatus]);
  const perStudentFeatureProjection = useMemo(() => projectPerStudentPlanFeatures(cms?.plan_matrix), [cms]);
  // WP-21: Offer markup from the LIVE billing catalogue (INR) — emitted only when real prices
  // exist; never a hard-coded price and never invented ratings/reviews.
  const pricingSchema = useMemo(() => {
    const offers = (cms?.pricing_integrity?.rows || [])
      .filter((row) => Number.isInteger(row?.billing_plan_paise))
      .map((row) => ({
        "@type": "Offer",
        name: `Vidyapeeth360 ${row.key.charAt(0).toUpperCase()}${row.key.slice(1)} plan`,
        price: String(Math.round(row.billing_plan_paise / 100)),
        priceCurrency: "INR",
        url: "https://vidyapeeth360.com/pricing",
      }));
    if (!offers.length) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Vidyapeeth360",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers,
      publisher: { "@id": ORGANIZATION_ID },
    };
  }, [cms]);
  const perStudentFeatureStatus = cms === null
    ? (cmsError ? PRICING_FEATURE_STATUS.ERROR : PRICING_FEATURE_STATUS.LOADING)
    : (perStudentFeatureProjection.valid ? PRICING_FEATURE_STATUS.READY : PRICING_FEATURE_STATUS.ERROR);

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="pricing-page">
      <MarketingSeo
        title="School ERP Pricing — Transparent Plans in ₹ | Vidyapeeth360"
        description="Compare published fixed monthly plans in ₹, the reviewed per-student proposal, payment-gateway charges, one-time integration costs and message rates."
        path="/pricing"
        image="/og/og-pricing.jpg"
        schema={pricingSchema}
      />
      <ScrollProgressBar />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main className="flex-1">
        {/* Header — WP-12: one line + one sentence, so the plan cards land in the first viewport. */}
        <section className="mx-auto max-w-6xl px-5 pt-10 sm:pt-14 pb-6 text-center">
          <h1 className="font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight max-w-2xl mx-auto">
            {cms?.pricing_page?.heading || "Two ways to buy. One price for your whole school."}
          </h1>
          <p className="mt-4 text-ink-2 text-[16px] sm:text-[17px] leading-relaxed max-w-2xl mx-auto">
            Choose a published fixed monthly plan for one school, or request a reviewed per-student
            proposal for a larger cohort or multi-campus rollout. Start free for 90 days — no card;
            {` ${taxDisplay}`}.
            {bandRangeLabel ? ` Reviewed per-student proposals currently start from ₹${Math.min(...bandRates) / 100} per month.` : " Per-student terms are confirmed in writing."}
          </p>
        </section>

        {/* Fixed plans */}
        <section className="mx-auto max-w-7xl px-5 pb-4" aria-label="Fixed plans">
          {cmsError && cms === null ? (
            <div className="max-w-md mx-auto py-10">
              <ErrorState title="Couldn't load live pricing" desc="Live prices didn’t load just now. Try again, or ask us for a written proposal with exact figures." onRetry={loadCms} retryLabel="Reload pricing" />
            </div>
          ) : cms === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-page-loading" aria-busy="true">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[520px] rounded-[28px]" />)}
            </div>
          ) : (
            /* WP-08 + WP-12: the cards are now the FIRST viewport of /pricing — they must paint
               at opacity 1, so no mount-play reveal here (a plain grid; PlanCard's hover motion
               is unaffected). */
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch pt-4" data-testid="pricing-page-grid">
              {fixedCards.map((pl) => (
                <PlanCard key={pl.id} pl={pl} navigate={navigate} reduceMotion={reduceMotion}
                  onContactSales={() => { track("demo_request", { placement: "pricing_enterprise" }); setSalesOpen(true); }} />
              ))}
            </div>
          )}
          {/* Reassurance row + commercial caveat — BELOW the cards (WP-12), one quiet line each. */}
          <ul className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-ink-2">
            {["90-day full-term free trial", "No credit card to start", taxDisplay, "Final terms captured in writing"].map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-center text-[13px] text-ink-subtle max-w-2xl mx-auto">
            Prices shown are the current self-serve billing base ({taxDisplay}). Annual terms and any
            negotiated limits are recorded in your agreement before you subscribe.
          </p>
        </section>

        <PricingCostDisclosure
          schedule={cms?.commercial_cost_schedule}
          loading={cms === null && !cmsError}
          error={cmsError || Boolean(cms && !cms?.commercial_cost_schedule)}
          onRetry={loadCms}
          onRequestReview={() => {
            track("demo_request", { placement: "pricing_cost_review" });
            setSalesOpen(true);
          }}
        />

        {/* Per-student model — the estimator inline (this page is the deep-dive surface). */}
        <section className="border-b border-line bg-surface" aria-labelledby="per-student-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-14">
            <div className="max-w-2xl">
              <h2 id="per-student-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight">
                Need a per-student proposal?
              </h2>
              <p className="mt-3 text-ink-2 text-[15px] leading-relaxed">
                Model a class-band proposal with the Growth plan feature set. This estimator is for
                planning and a reviewed proposal; current self-serve billing remains plan-based.
              </p>
            </div>
            <div className="mt-8">
              {cms === null && !cmsError ? (
                <div role="status" aria-busy="true" aria-label="Loading per-student proposal estimator">
                  <Skeleton className="h-[520px] rounded-[28px]" />
                </div>
              ) : perStudentPricingVerified ? (
                <PricingEstimator pricingModel={cms.pricing_model} embedded taxDisplay={taxDisplay} />
              ) : (
                <ErrorState title="Proposal rates are unavailable" desc="We can’t show proposal rates right now. Rather than show a figure we can’t stand behind, we’ve hidden it — reload, or ask us for a written proposal." onRetry={loadCms} retryLabel="Reload proposal rates" />
              )}
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => { track("pricing_per_student_open", { placement: "pricing_page" }); setPerStudentOpen(true); }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary"
                data-testid="pricing-page-per-student"
              >
                See what a per-student proposal includes <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* Full catalogue — core, sellable entitlements and release-controlled capability are kept distinct. */}
        {cms !== null && (
          <section className="mx-auto max-w-6xl px-5 py-12 sm:py-14" aria-label="Compare all plans, feature by feature">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-[30px] sm:text-[34px] text-ink tracking-tight">Compare all plans, feature by feature</h2>
              <p className="text-ink-2 mt-2.5 max-w-2xl mx-auto">The always-on core every school gets, what each plan adds, and the capabilities still being rolled out — shown separately, so there are no surprises.</p>
            </div>
            <PricingFeatureCatalogue onRetry={loadCms} planMatrix={cms.plan_matrix} plans={matrixPlans} />
          </section>
        )}

        {/* The terms a committee actually asks about — registry-verified facts only. */}
        <section className="border-y border-line bg-surface" aria-labelledby="pricing-terms-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-14">
            <h2 id="pricing-terms-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight max-w-2xl">
              The fine print, in large print.
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {/* Copy is CMS-owned (cms.pricing_page.terms); TERMS is the byte-mirrored fallback.
                  Icons stay code-owned, matched by position (extra CMS terms reuse the shield). */}
              {(cms?.pricing_page?.terms?.length
                ? cms.pricing_page.terms.map((term, index) => [TERMS[index]?.[0] || ShieldCheck, term.title, term.body])
                : TERMS
              ).map(([Icon, t, d]) => (
                <div key={t} className="flex items-start gap-4 rounded-2xl border border-line bg-canvas p-5">
                  <span className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-primary" aria-hidden="true" /></span>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-ink text-[15px] leading-snug">{t}</h3>
                    <p className="mt-1.5 text-[14px] text-ink-2 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink-2 inline-flex items-center gap-1.5">
              <HelpCircle className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              More questions? <Link to="/#faq" className="font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Read the FAQ</Link> or{" "}
              <Link to="/contact" className="font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">talk to a real person</Link>.
            </p>
          </div>
        </section>

        {/* Close */}
        {/* pb clears the fixed Aira launcher at narrow widths (audit: it covered the last card CTA) */}
        <section className="mx-auto max-w-6xl px-5 pt-14 sm:pt-16 pb-24 lg:pb-16 text-center">
          <h2 className="font-heading font-bold text-[26px] sm:text-[30px] text-ink tracking-tight max-w-xl mx-auto leading-tight">
            Take a full term to decide.
          </h2>
          <p className="text-ink-2 mt-3 max-w-lg mx-auto leading-relaxed">
            Register free, preview a student CSV/XLSX import where Migration Copilot is included,
            and pick a plan only after the school has evaluated its required workflows.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <ButtonLink to="/register" size="lg" onClick={() => track("trial_cta_click", { placement: "pricing_page" })} data-testid="pricing-page-register">
              {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </ButtonLink>
            <Button size="lg" variant="outline" onClick={() => { track("demo_request", { placement: "pricing_page" }); setSalesOpen(true); }} data-testid="pricing-page-demo">
              Book a guided demo
            </Button>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="plans, charges and your rollout scope" placement="pricing_page" />
      <PerStudentPricingModal
        coreModules={perStudentFeatureProjection.coreModules}
        featureStatus={perStudentFeatureStatus}
        includedFeatures={perStudentFeatureProjection.includedFeatures}
        onClose={() => setPerStudentOpen(false)}
        onPrimaryAction={() => {
          track("demo_request", { placement: "per_student_proposal" });
          setPerStudentOpen(false);
          setSalesOpen(true);
        }}
        onRetry={loadCms}
        open={perStudentOpen}
        primaryActionLabel="Request reviewed proposal"
        pricingModel={cms?.pricing_model}
        pricingProvenance={cms?.pricing_model_provenance}
        pricingStatus={cmsError
          ? PRICING_FEATURE_STATUS.ERROR
          : (cms === null ? PRICING_FEATURE_STATUS.LOADING : PRICING_FEATURE_STATUS.READY)}
        taxDisplay={taxDisplay}
      />
    </div>
    </MarketingMotion>
  );
}
